import { useEffect, useState } from 'react'
import useStore from '../../Store'
import {
    Alert,
    Button,
    Box,
    Link,
    Header,
    Modal,
    SpaceBetween,
    Flashbar,
    TextContent,
    SegmentedControl,
} from '@amzn/awsui-components-react'
import AllocationAddNewProjectTable from './AllocationAddNewProjectTable'
import { useAppContext } from '../../../context'
import { getTotalByKeyList, getYearFromDate } from '../reusable/AllocationUtils'
import { allocationCreateAddSegmentControl, isProgramProjectActiveForYear } from '../../common/Util'
import {
    NewProject,
    NewServiceProject,
    createProjects,
    getNewProjectPayload,
    validateCreateInputs,
} from '../project/projectUtil'
import { AlertTypes, EMPLOYEES_GENERIC, getDefaultAllocation } from '../../Constant'
import {
    ADMIN_ACCESS_REQUEST,
    CREATE_PROJECT_REQUEST,
    FALCON_SLACK_CHANNEL,
    SLACK_AMAZON_OPERATIONS,
} from '../../common/LinkUtil'
import CreateProjectModalContent from '../project/CreateProjectModalContent'
import _ from 'lodash'

const AllocationTableHeader = (props) => {
    const { allocationDisabled } = props

    const appContext = useAppContext()
    const apiClient = appContext.apiClient
    const userAlias = appContext.userAlias
    const canAdmin = useStore((state) => state.canAdmin)

    const selectedProjectsToRemove = useStore((state) => state.selectedProjectsToRemove)
    const setSelectedProjectsToRemove = useStore((state) => state.setSelectedProjectsToRemove)
    const selectedProjectsToAdd = useStore((state) => state.selectedProjectsToAdd)
    const setSelectedProjectsToAdd = useStore((state) => state.setSelectedProjectsToAdd)
    const allocationProjects = useStore((state) => state.allocationProjects)
    const setAllocationProjects = useStore((state) => state.setAllocationProjects)
    const allRemainProjects = useStore((state) => state.allRemainProjects)
    const setAllRemainProjects = useStore((state) => state.setAllRemainProjects)
    const setTotalAllocationByEmployeeType = useStore(
        (state) => state.setTotalAllocationByEmployeeType,
    )
    const setTotalAllocation = useStore((state) => state.setTotalAllocation)
    const setAllocationAddProjectAlert = useStore((state) => state.setAllocationAddProjectAlert)
    const selectBusinessEntity = useStore((state) => state.selectBusinessEntity)
    const allocationEndDate = useStore((state) => state.allocationEndDate)
    const actualsYear = getYearFromDate(allocationEndDate)

    const [visible, setVisible] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [newProjects, setNewProjects] = useState<(NewProject | NewServiceProject)[]>([])
    const [createProjectAlertItems, setCreateProjectAlertItems] = useState<object[]>([])
    const setHistorySelectedYear = useStore((state) => state.setHistorySelectedYear)

    useEffect(() => {
        if (actualsYear) {
            setHistorySelectedYear(actualsYear)
        }
    }, [actualsYear])
    const [selectProjectSegmentedControl, setSelectProjectSegmentedControl] = useState<string>(
        allocationCreateAddSegmentControl[0].id,
    )

    const isProjectCreationRestricted =
        !canAdmin && !selectBusinessEntity.configurations.project.non_admin_creation

    useEffect(() => {
        if (visible && selectProjectSegmentedControl === allocationCreateAddSegmentControl[0].id) {
            getAllProjectsByBusinessEntity()
        }
    }, [visible, selectProjectSegmentedControl])

    const filterProjectsBySelectedProjects = (targetProjects, selectedProjects) => {
        let tempProjects = [...targetProjects]
        tempProjects = tempProjects.filter(
            (prj1) => !selectedProjects.find((prj2) => prj1.project_id === prj2.project_id),
        )
        return tempProjects
    }

    const handleModalClose = () => {
        setVisible(false)
        setCreateProjectAlertItems([])
        setSelectedProjectsToAdd([])
    }

    const handleAddProjects = (
        projectsToAdd = selectedProjectsToAdd,
        remainProjects = allRemainProjects,
    ) => {
        let tempAllocationProjects = [...allocationProjects]
        tempAllocationProjects = tempAllocationProjects.concat(
            projectsToAdd.map((prj) => {
                prj.allocation_value = '0'
                prj.allocation_by_employee_type = getDefaultAllocation()
                return prj
            }),
        )
        setAllocationProjects(tempAllocationProjects)
        setSelectedProjectsToAdd([])
        setAllRemainProjects(filterProjectsBySelectedProjects(remainProjects, projectsToAdd))
        if (selectedProjectsToRemove.length !== 0) {
            setSelectedProjectsToRemove([])
        }
        setAllocationAddProjectAlert({
            type: 'success',
            content: (
                <TextContent>
                    {`Successfully added project(s) ${projectsToAdd.map(
                        (prj) => prj.project_name,
                    )} to the allocation table.`}
                </TextContent>
            ),
        })
        setVisible(false)
    }

    const handleRemoveProject = () => {
        const tempAllocationProjects = filterProjectsBySelectedProjects(
            allocationProjects,
            selectedProjectsToRemove,
        )
        setAllocationProjects(tempAllocationProjects)
        setTotalAllocationByEmployeeType(
            Object.fromEntries(
                EMPLOYEES_GENERIC.map((employee) => [
                    employee.type,
                    getTotalByKeyList(
                        tempAllocationProjects,
                        `allocation_by_employee_type.${employee.type}`,
                    ),
                ]),
            ),
        )
        setTotalAllocation(getTotalByKeyList(tempAllocationProjects, 'allocation_value'))
        const tempAllRemainProjects = [...allRemainProjects]
        tempAllRemainProjects.concat(selectedProjectsToRemove)
        setAllRemainProjects(tempAllRemainProjects)
        setSelectedProjectsToRemove([])
        if (selectedProjectsToAdd.length !== 0) {
            setSelectedProjectsToAdd([])
        }
        setAllocationAddProjectAlert({
            type: 'success',
            content: `Successfully removed project(s) ${selectedProjectsToRemove.map(
                (prj) => prj.project_name,
            )} from the allocation table.`,
        })
    }

    const getAllProjectsByBusinessEntity = () => {
        if (!actualsYear) {
            console.error(
                `Invalid actuals year ${actualsYear}, full date ${allocationEndDate}, cannot fetch projects.`,
            )
            setAllRemainProjects([])
            return
        }
        setIsLoading(true)
        apiClient
            .get(`/business-entity/${selectBusinessEntity.id}/year/${actualsYear}/projects`)
            .then((response) => {
                const allProjects = response.data
                setAllRemainProjects(getAllRemainProjectsList(allProjects))
                setIsLoading(false)
            })
            .catch((error) => {
                console.error(error)
                setIsLoading(false)
            })
    }

    const getAllRemainProjectsList = (allProjects: any) => {
        return allProjects.filter(
            (proj) =>
                !allocationProjects.find(({ project_id }) => proj.project_id === project_id) &&
                isProgramProjectActiveForYear(proj),
        )
    }

    const setAlertItemsTemplate = (type: string, content: string) => {
        setCreateProjectAlertItems([
            {
                type: type,
                content: content,
                dismissible: true,
                dismissLabel: 'Dismiss message',
                onDismiss: () => setCreateProjectAlertItems([]),
            },
        ])
    }

    const handleNewProjectReset = () => {
        setNewProjects([])
    }

    const handleProjectsCreation = () => {
        const payload = { requester: userAlias }
        payload['projects'] = getNewProjectPayload(
            actualsYear,
            newProjects,
            selectBusinessEntity.id,
        )

        if (payload['projects'].length === 0) {
            handleAddProjects()
            return
        }

        setIsLoading(true)
        createProjects(apiClient, payload)
            .then(() => {
                const newSelectProjectsToAdd = [...selectedProjectsToAdd, ...payload['projects']]
                const newAllRemainProjects = [...allRemainProjects, ...payload['projects']]
                setSelectedProjectsToAdd(newSelectProjectsToAdd)
                setAllRemainProjects(newAllRemainProjects)

                // Add the newly created projects to the allocation
                handleAddProjects(newSelectProjectsToAdd, newAllRemainProjects)
                handleNewProjectReset()
                setAlertItemsTemplate(
                    AlertTypes.SUCCESS,
                    `Successfully created and select project ${newProjects[0].projectName}.`,
                )
            })
            .catch((error) => {
                setAlertItemsTemplate(
                    AlertTypes.ERROR,
                    `Failed to create project ${newProjects[0].projectName}: ${error.response.data}.`,
                )
                console.error(error)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const selectProjectsModalContents = () => {
        return (
            <SpaceBetween direction='vertical' size='s'>
                <Alert type={AlertTypes.INFO} header='Cannot find your project after searching?'>
                    {isProjectCreationRestricted ? (
                        <SpaceBetween direction='horizontal' size='xxs'>
                            Contact
                            <Link
                                external
                                externalIconAriaLabel='Opens in a new tab'
                                href={
                                    SLACK_AMAZON_OPERATIONS +
                                    selectBusinessEntity.slack_channels[0]?.slack_channel_id
                                }
                            >
                                #{selectBusinessEntity.slack_channels[0]?.slack_channel_name}
                            </Link>
                            {`for more information, request new project `}
                            <Link
                                external
                                externalIconAriaLabel='Opens in a new tab'
                                href={CREATE_PROJECT_REQUEST}
                            >
                                here
                            </Link>
                            , or request admin access
                            <Link
                                external
                                externalIconAriaLabel='Opens in a new tab'
                                href={ADMIN_ACCESS_REQUEST}
                            >
                                here
                            </Link>
                            .
                        </SpaceBetween>
                    ) : (
                        <SpaceBetween direction='horizontal' size='xxs'>
                            Click "Create Project(s)" tab and create one, direct to projects page
                            <Link
                                external
                                externalIconAriaLabel='Opens in a new tab'
                                href={'/projects'}
                            >
                                here
                            </Link>
                            , or contact
                            <Link
                                external
                                externalIconAriaLabel='Opens in a new tab'
                                href={FALCON_SLACK_CHANNEL}
                            >
                                #falcon-resource-allocation-help-and-requests
                            </Link>
                            for more information.
                        </SpaceBetween>
                    )}
                </Alert>
                <AllocationAddNewProjectTable modalVisible={visible} isLoading={isLoading} />
            </SpaceBetween>
        )
    }

    return (
        <Box>
            <Modal
                onDismiss={() => handleModalClose()}
                visible={visible}
                closeAriaLabel='Close modal'
                size='max'
                header='Add / Select Project(s)'
                footer={
                    <Box float='right'>
                        <SpaceBetween direction='horizontal' size='xs'>
                            <Button variant='link' onClick={() => handleModalClose()}>
                                Cancel
                            </Button>
                            <Button
                                variant='primary'
                                onClick={handleProjectsCreation}
                                disabled={
                                    (_.isEmpty(selectedProjectsToAdd) && _.isEmpty(newProjects)) ||
                                    !validateCreateInputs(newProjects)
                                }
                                data-cy-allocation={'Select Projects'}
                            >
                                Confirm Projects
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <SpaceBetween direction='vertical' size='s'>
                    <Flashbar items={createProjectAlertItems} />
                    <SegmentedControl
                        selectedId={selectProjectSegmentedControl}
                        onChange={({ detail }) =>
                            setSelectProjectSegmentedControl(detail.selectedId)
                        }
                        options={allocationCreateAddSegmentControl}
                    />
                    {selectProjectSegmentedControl === allocationCreateAddSegmentControl[0].id ? (
                        selectProjectsModalContents()
                    ) : (
                        <SpaceBetween direction='vertical' size='s'>
                            <Alert type={AlertTypes.INFO}>
                                Projects added below will be created and added to the allocation
                                once confirmed.
                            </Alert>
                            <CreateProjectModalContent
                                preSelectedProgram={null}
                                programMetadata={{}}
                                selectedYear={actualsYear}
                                newProjects={newProjects}
                                setNewProjects={setNewProjects}
                                canAdmin={canAdmin}
                                canCreateProject={
                                    selectBusinessEntity.configurations.project.non_admin_creation
                                }
                            />
                        </SpaceBetween>
                    )}
                </SpaceBetween>
            </Modal>
            <Header
                actions={
                    <SpaceBetween size='s' direction='horizontal'>
                        <Button
                            onClick={() => setVisible(true)}
                            disabled={allocationDisabled()}
                            data-cy-allocation={'Add / Select Project'}
                        >
                            Add / Select Project(s)
                        </Button>
                        <Button
                            onClick={handleRemoveProject}
                            disabled={selectedProjectsToRemove.length === 0 || allocationDisabled()}
                            data-cy-allocation={'Remove Projects'}
                        >
                            Remove Project(s)
                        </Button>
                    </SpaceBetween>
                }
            ></Header>
        </Box>
    )
}

export default AllocationTableHeader
